import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Show } from "@clerk/react";
import { ShieldCheck, Calculator, FileScan, ScrollText } from "lucide-react";
import { useDomTranslator } from "../lib/domTranslate";
import { LoginAsPicker } from "../components/LoginAs";

const POINTS = [
  [Calculator, "EPF & ESIC auto-calculated", "12% PF on ₹25,000 ceiling, 0.75% / 3.25% ESIC under ₹21,000"],
  [FileScan, "AI challan reading", "TRRN, amount and headcount pulled from every challan"],
  [ShieldCheck, "Payment hold engine", "Block vendor disbursement until statutory dues reconcile"],
  [ScrollText, "Form IV / XV / 13", "Print-ready Register of Wages in one click"],
];

export default function AuthShell({ children, picker }) {
  const [lang, setLang] = useState(localStorage.getItem("c369-lang") || "en");
  useDomTranslator(lang);
  const pick = (l) => { setLang(l); localStorage.setItem("c369-lang", l); window.dispatchEvent(new Event("c369-lang")); };
  return (
    <div className="min-h-screen flex">
      <Show when="signed-in"><Navigate to="/" replace /></Show>
      <div className="hidden lg:flex flex-1 flex-col justify-between p-12 c-grid-bg border-r border-[#1F2A3E] relative overflow-hidden">
        <div className="absolute -top-40 -left-40 h-96 w-96 rounded-full bg-[#00E599]/10 blur-3xl" />
        <div className="flex items-center gap-2.5 relative">
          <div className="h-9 w-9 rounded-lg bg-[#00E599] text-[#0B1017] grid place-items-center font-display font-extrabold">369</div>
          <span className="font-display font-extrabold text-xl">Comply369</span>
        </div>
        <div className="relative max-w-xl">
          <div className="c-eyebrow mb-4">Contract labour compliance</div>
          <h1 className="text-5xl font-extrabold tracking-tight leading-[1.05]">
            Every vendor. Every worker.<br /><span className="text-[#00E599]">Every rupee reconciled.</span>
          </h1>
          <div className="grid grid-cols-2 gap-4 mt-12">
            {POINTS.map(([Icon, t, d], i) => (
              <div key={t} className="c-surface p-5 c-rise" style={{ animationDelay: `${i * 80}ms` }}>
                <Icon size={18} className="text-[#00E599] mb-3" />
                <div className="font-semibold text-sm">{t}</div>
                <div className="text-xs text-slate-400 mt-1 leading-relaxed">{d}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="text-xs text-slate-600 font-mono relative">CLRA · EPF · ESIC · DPDP 2023</div>
      </div>
      <div className="flex-1 flex flex-col items-center justify-center p-6 gap-6">
        <div className="lg:hidden flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-lg bg-[#00E599] text-[#0B1017] grid place-items-center font-display font-extrabold">369</div>
          <span className="font-display font-extrabold text-xl">Comply369</span>
        </div>
        {picker ? <LoginAsPicker>{children}</LoginAsPicker> : <div data-testid="auth-form-container">{children}</div>}
        <div className="flex flex-wrap justify-center gap-1" translate="no" data-testid="auth-language-switcher">
          {[["en", "English"], ["hi", "हिन्दी"], ["gu", "ગુજરાતી"], ["mr", "मराठी"], ["te", "తెలుగు"]].map(([k, l]) => (
            <button key={k} onClick={() => pick(k)} data-testid={`auth-lang-${k}`}
              className={`px-2.5 py-1 rounded-md text-xs ${lang === k ? "bg-[#00E599]/15 text-[#00E599]" : "text-slate-400 hover:text-white"}`}>{l}</button>))}
        </div>
      </div>
    </div>
  );
}
